import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Settings, TrendingUp, TrendingDown, Building } from 'lucide-react';
import { useEmpresa } from '@/context/EmpresaContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

export default function Configuracion() {
  const { API } = useEmpresa();
  const [activeTab, setActiveTab] = useState('tipos-lp');
  
  // Tipos LP
  const [tiposLP, setTiposLP] = useState([]);
  const [loadingLP, setLoadingLP] = useState(true);
  
  // Tipos CP
  const [tiposCP, setTiposCP] = useState([]);
  const [loadingCP, setLoadingCP] = useState(true);
  
  // Entidades
  const [entidades, setEntidades] = useState([]);
  const [loadingEnt, setLoadingEnt] = useState(true);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'lp', 'cp', 'entidad'
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', activo: true });
  const [isEditing, setIsEditing] = useState(false);

  // Fetch functions
  const fetchTiposLP = async () => {
    try {
      const response = await axios.get(`${API}/tipos-producto-lp`);
      setTiposLP(response.data);
    } catch (error) {
      console.error('Error fetching tipos LP:', error);
    } finally {
      setLoadingLP(false);
    }
  };

  const fetchTiposCP = async () => {
    try {
      const response = await axios.get(`${API}/tipos-producto-cp`);
      setTiposCP(response.data);
    } catch (error) {
      console.error('Error fetching tipos CP:', error);
    } finally {
      setLoadingCP(false);
    }
  };

  const fetchEntidades = async () => {
    try {
      const response = await axios.get(`${API}/entidades-financieras`);
      setEntidades(response.data);
    } catch (error) {
      console.error('Error fetching entidades:', error);
    } finally {
      setLoadingEnt(false);
    }
  };

  useEffect(() => {
    fetchTiposLP();
    fetchTiposCP();
    fetchEntidades();
  }, [API]);

  // Dialog handlers
  const handleOpenDialog = (type, item = null) => {
    setDialogType(type);
    if (item) {
      setFormData({ nombre: item.nombre, activo: item.activo !== false });
      setSelectedItem(item);
      setIsEditing(true);
    } else {
      setFormData({ nombre: '', activo: true });
      setSelectedItem(null);
      setIsEditing(false);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      let endpoint = '';
      if (dialogType === 'lp') endpoint = 'tipos-producto-lp';
      else if (dialogType === 'cp') endpoint = 'tipos-producto-cp';
      else endpoint = 'entidades-financieras';

      if (isEditing && selectedItem) {
        await axios.put(`${API}/${endpoint}/${selectedItem.id}`, formData);
        toast.success('Actualizado correctamente');
      } else {
        await axios.post(`${API}/${endpoint}`, formData);
        toast.success('Creado correctamente');
      }
      
      setDialogOpen(false);
      if (dialogType === 'lp') fetchTiposLP();
      else if (dialogType === 'cp') fetchTiposCP();
      else fetchEntidades();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar');
    }
  };

  const openDeleteDialog = (type, item) => {
    setDialogType(type);
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      let endpoint = '';
      if (dialogType === 'lp') endpoint = 'tipos-producto-lp';
      else if (dialogType === 'cp') endpoint = 'tipos-producto-cp';
      else endpoint = 'entidades-financieras';

      await axios.delete(`${API}/${endpoint}/${selectedItem.id}`);
      toast.success('Eliminado correctamente');
      setDeleteDialogOpen(false);
      
      if (dialogType === 'lp') fetchTiposLP();
      else if (dialogType === 'cp') fetchTiposCP();
      else fetchEntidades();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error al eliminar');
    }
  };

  const getDialogTitle = () => {
    const action = isEditing ? 'Editar' : 'Nuevo';
    if (dialogType === 'lp') return `${action} Tipo Producto Largo Plazo`;
    if (dialogType === 'cp') return `${action} Tipo Producto Corto Plazo`;
    return `${action} Entidad Financiera`;
  };

  const renderTable = (data, type, loading) => (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <Table data-testid={`table-${type}`}>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="font-medium text-slate-500">Nombre</TableHead>
            <TableHead className="font-medium text-slate-500 text-center">Estado</TableHead>
            <TableHead className="font-medium text-slate-500 text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3} className="text-center py-10">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                </div>
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-center py-10 text-slate-500">
                No hay registros
              </TableCell>
            </TableRow>
          ) : (
            data.map((item) => (
              <TableRow key={item.id} className="hover:bg-slate-50" data-testid={`row-${type}-${item.id}`}>
                <TableCell className="font-medium">{item.nombre}</TableCell>
                <TableCell className="text-center">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                    item.activo !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {item.activo !== false ? 'Activo' : 'Inactivo'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(type, item)}
                      data-testid={`edit-${type}-${item.id}`}
                    >
                      <Pencil className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteDialog(type, item)}
                      data-testid={`delete-${type}-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="configuracion-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
          Configuración
        </h1>
        <p className="text-slate-500 mt-1">
          Parametriza los tipos de producto y entidades financieras
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100" data-testid="config-tabs">
          <TabsTrigger value="tipos-lp" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Tipos Largo Plazo
          </TabsTrigger>
          <TabsTrigger value="tipos-cp" className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4" />
            Tipos Corto Plazo
          </TabsTrigger>
          <TabsTrigger value="entidades" className="flex items-center gap-2">
            <Building className="w-4 h-4" />
            Entidades Financieras
          </TabsTrigger>
        </TabsList>

        {/* Tipos LP Tab */}
        <TabsContent value="tipos-lp" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Configura los tipos de producto disponibles para operaciones a largo plazo
            </p>
            <Button 
              onClick={() => handleOpenDialog('lp')} 
              className="btn-primary flex items-center gap-2"
              data-testid="add-tipo-lp-btn"
            >
              <Plus className="w-4 h-4" />
              Añadir Tipo
            </Button>
          </div>
          {renderTable(tiposLP, 'lp', loadingLP)}
        </TabsContent>

        {/* Tipos CP Tab */}
        <TabsContent value="tipos-cp" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Configura los tipos de producto disponibles para operaciones a corto plazo
            </p>
            <Button 
              onClick={() => handleOpenDialog('cp')} 
              className="btn-primary flex items-center gap-2"
              data-testid="add-tipo-cp-btn"
            >
              <Plus className="w-4 h-4" />
              Añadir Tipo
            </Button>
          </div>
          {renderTable(tiposCP, 'cp', loadingCP)}
        </TabsContent>

        {/* Entidades Tab */}
        <TabsContent value="entidades" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Configura las entidades financieras disponibles
            </p>
            <Button 
              onClick={() => handleOpenDialog('entidad')} 
              className="btn-primary flex items-center gap-2"
              data-testid="add-entidad-btn"
            >
              <Plus className="w-4 h-4" />
              Añadir Entidad
            </Button>
          </div>
          {renderTable(entidades, 'entidad', loadingEnt)}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-config">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {getDialogTitle()}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Nombre"
                data-testid="input-nombre-config"
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="activo"
                checked={formData.activo}
                onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
                data-testid="switch-activo-config"
              />
              <Label htmlFor="activo">Activo</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-config">
                {isEditing ? 'Guardar Cambios' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog-config">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El registro será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
